import React, { useCallback, useEffect, useState } from 'react';
import {
  FiArrowRight,
  FiCheckCircle,
  FiCopy,
  FiHelpCircle,
  FiLink,
  FiMail,
  FiMessageSquare,
  FiPackage,
  FiX,
} from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import PlanUpgradeCard from './PlanUpgradeCard';

const STEPS = [
  {
    id: 'pack',
    icon: FiPackage,
    title: 'Pack del mes',
    short: 'ZIP con ventas, egresos e impagas',
  },
  {
    id: 'link',
    icon: FiLink,
    title: 'Link de solo lectura',
    short: 'Vista contador sin login (magic link)',
  },
  {
    id: 'notas',
    icon: FiMessageSquare,
    title: 'Notas del contador',
    short: 'Instrucciones que ves en Inicio',
  },
];

function HelpModal({ open, onClose, modoOscuro, onNavigate }) {
  if (!open) return null;
  const overlay = modoOscuro ? 'bg-black/70' : 'bg-black/50';
  const panel = modoOscuro ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900';
  const muted = modoOscuro ? 'text-gray-400' : 'text-gray-600';

  return (
    <div className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 ${overlay}`} role="dialog" aria-modal="true">
      <div className={`w-full max-w-lg rounded-2xl border shadow-xl ${panel}`}>
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-inherit">
          <h3 className="text-sm font-semibold">Cómo trabaja tu contador con Contsuite</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/10" aria-label="Cerrar">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <ol className="px-4 py-4 space-y-4 text-sm">
          <li>
            <p className="font-semibold">1. Pack mensual (ZIP)</p>
            <p className={`mt-1 text-xs ${muted}`}>
              Cargá el email del estudio en Cuenta → Notificaciones. Desde el checklist de cierre de mes podés enviar el pack al instante o dejarlo programado.
            </p>
          </li>
          <li>
            <p className="font-semibold">2. Link de solo lectura</p>
            <p className={`mt-1 text-xs ${muted}`}>
              Generá un magic link para que vea ventas y cobranzas sin usuario. Ideal para WhatsApp; vence en unos días y podés rotarlo.
            </p>
          </li>
          <li>
            <p className="font-semibold">3. Notas del contador</p>
            <p className={`mt-1 text-xs ${muted}`}>
              Si invitás un Lector, puede dejarte notas operativas que aparecen acá en Inicio (sin datos sensibles).
            </p>
          </li>
        </ol>
        <div className="px-4 pb-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { onClose(); onNavigate('/cuenta?tab=notifications'); }}
            className="rounded-xl bg-[#1F80FF] px-3 py-2 text-xs font-semibold text-white hover:bg-[#1866CC]"
          >
            Configurar email del contador
          </button>
          <button
            type="button"
            onClick={() => { onClose(); onNavigate('/contador'); }}
            className={`rounded-xl border px-3 py-2 text-xs font-semibold ${modoOscuro ? 'border-gray-600' : 'border-gray-300'}`}
          >
            Vista contador
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ContadorPuenteCard({
  hasProfesional,
  modoOscuro,
  box,
  textPri,
  textSec,
  textMuted,
  onNavigate,
  onGoToCierre,
  visible = true,
}) {
  const { secureFetch } = useApi();
  const [helpOpen, setHelpOpen] = useState(false);
  const [guestUrl, setGuestUrl] = useState('');
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestMsg, setGuestMsg] = useState('');
  const [cierrePercent, setCierrePercent] = useState(null);
  const [contadorEmail, setContadorEmail] = useState('');

  const loadStatus = useCallback(async () => {
    if (!hasProfesional) return;
    try {
      const [cierreRes, prefRes] = await Promise.all([
        secureFetch('/api/reportes/cierre-mes'),
        secureFetch('/api/notifications/preferences'),
      ]);
      if (cierreRes.ok) {
        const cierre = await cierreRes.json();
        setCierrePercent(typeof cierre.percent === 'number' ? cierre.percent : null);
      }
      if (prefRes.ok) {
        const prefs = await prefRes.json();
        setContadorEmail(String(prefs.contadorEmail || '').trim());
      }
    } catch {
      /* silencioso */
    }
  }, [hasProfesional, secureFetch]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  if (!visible) return null;

  if (!hasProfesional) {
    return (
      <PlanUpgradeCard
        compact
        description="Pack al contador, link de solo lectura y notas del estudio con plan Profesional+."
        planLabel="Profesional"
        modoOscuro={modoOscuro}
      />
    );
  }

  const generateGuestLink = async () => {
    setGuestLoading(true);
    setGuestMsg('');
    try {
      const res = await secureFetch('/api/contador/guest-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rotate: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No se pudo generar el link');
      setGuestUrl(data.url || '');
      setGuestMsg(data.message || 'Link listo.');
    } catch (err) {
      setGuestMsg(err.message || 'Error al generar link');
    } finally {
      setGuestLoading(false);
    }
  };

  const copyGuestLink = async () => {
    if (!guestUrl) return;
    try {
      await navigator.clipboard.writeText(guestUrl);
      setGuestMsg('Link copiado al portapapeles.');
    } catch {
      setGuestMsg('Copiá el link manualmente.');
    }
  };

  const goCierre = () => {
    if (typeof onGoToCierre === 'function') {
      onGoToCierre();
      return;
    }
    onNavigate('/inicio#month-close-checklist');
  };

  return (
    <>
      <section className={`rounded-2xl border overflow-hidden ${box}`} id="contador-puente">
        <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                modoOscuro ? 'bg-violet-900/40 text-violet-200' : 'bg-violet-50 text-violet-800'
              }`}>
                <FiMail className="w-3 h-3" />
                Puente contador
              </span>
              <h2 className={`text-sm font-semibold mt-1.5 ${textPri}`}>Enviá el mes ordenado al estudio</h2>
              <p className={`text-xs mt-0.5 ${textSec}`}>
                Cobraste y registraste acá; tu contador deja de pedir planillas por WhatsApp.
                {cierrePercent != null && cierrePercent < 100 && (
                  <span className={`block mt-0.5 ${textMuted}`}>Cierre del mes: {cierrePercent}% listo.</span>
                )}
                {contadorEmail && (
                  <span className={`block mt-0.5 ${textMuted}`}>Pack configurado para {contadorEmail}</span>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className={`inline-flex items-center gap-1 text-xs font-semibold shrink-0 ${modoOscuro ? 'text-violet-300' : 'text-violet-700'} hover:underline`}
            >
              <FiHelpCircle className="w-3.5 h-3.5" />
              Cómo funciona
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {STEPS.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`rounded-xl border px-3 py-2.5 ${
                    modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50/80'
                  }`}
                >
                  <Icon className={`w-4 h-4 mb-1 ${modoOscuro ? 'text-violet-300' : 'text-violet-600'}`} />
                  <p className={`text-xs font-semibold ${textPri}`}>{step.title}</p>
                  <p className={`text-[10px] mt-0.5 ${textMuted}`}>{step.short}</p>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap gap-2">
            <button
              type="button"
              onClick={goCierre}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#1F80FF] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#1866CC]"
            >
              Cerrar mes y enviar pack
              <FiArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              disabled={guestLoading}
              onClick={generateGuestLink}
              className={`rounded-xl border px-4 py-2.5 text-xs font-semibold disabled:opacity-60 ${
                modoOscuro ? 'border-gray-600 text-gray-200' : 'border-gray-300 text-gray-800'
              }`}
            >
              {guestLoading ? 'Generando…' : guestUrl ? 'Actualizar link' : 'Obtener link para el contador'}
            </button>
          </div>

          {guestUrl && (
            <div className={`flex flex-col sm:flex-row gap-2 rounded-xl border px-3 py-2 ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
              <input
                type="text"
                readOnly
                value={guestUrl}
                className={`flex-1 min-w-0 text-xs bg-transparent outline-none ${textPri}`}
              />
              <button
                type="button"
                onClick={copyGuestLink}
                className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-[#1F80FF] hover:underline shrink-0"
              >
                <FiCopy className="w-3.5 h-3.5" />
                Copiar
              </button>
            </div>
          )}

          {guestMsg && (
            <p className={`text-xs flex items-start gap-1.5 ${modoOscuro ? 'text-green-300' : 'text-green-700'}`}>
              <FiCheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {guestMsg}
            </p>
          )}
        </div>
      </section>

      <HelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        modoOscuro={modoOscuro}
        onNavigate={onNavigate}
      />
    </>
  );
}
